import * as React from "react";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
  ExpandedState,
  getExpandedRowModel,
} from "@tanstack/react-table";
import { Input } from "components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import TablePagination from "./table-pagination";
import { useEffect, useState } from "react";
import { getOrderData, markOrderAsDone, Order } from "action/order-action";
import { toast } from "sonner";
import Loading from "components/loading";
import { Copy, Megaphone, ShoppingBag } from "lucide-react";
import { useStoreLink } from "lib/store-link";
import { useOpenBoost } from "lib/boost";
import { Checkbox } from "components/ui/checkbox";
import { Button } from "components/ui/button";
import PageHeader from "components/page-header";
import { Card } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { CURRENCY } from "helpers/constants";
// Import CSSTransition and TransitionGroup from react-transition-group
import { CSSTransition, TransitionGroup } from "react-transition-group";
import "./StorboltOrderListingComponent.css";
import { useSessionControl } from "hooks/use-session-control"; // Import your CSS for animations

const StorboltOrderListingComponent = () => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [data, setData] = React.useState<Order[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const { sessionControl } = useSessionControl();

  useEffect(() => {
    setIsLoading(true);
    getOrderData()
      .then((response) => {
        if (response.status === 200) {
          setData(response.body);
          if (response.message) {
            toast.error(response.message);
          }
        } else {
          sessionControl(response.status);
          toast.error(response.error);
        }
        setIsLoading(false);
      })
      .catch((err: any) => {
        toast.error("Failed to get orders, an error occurred");
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkAsDone = (order: Order) => {
    setSelectedOrder(order);
    setShowDialog(true);
  };

  const confirmMarkAsDone = () => {
    if (selectedOrder) {
      const toastId = toast.loading("Marking order as done...");
      // Remove the order from data
      setData((prevData) =>
        prevData.filter((order) => order.id !== selectedOrder.id),
      );
      setShowDialog(false);
      setSelectedOrder(null);

      markOrderAsDone(selectedOrder.id)
        .then((response) => {
          if (response.status === 200) {
            toast.success("Order marked as done successfully", {
              id: toastId,
            });
          } else {
            sessionControl(response.status);
            toast.error(response.error, { id: toastId });
          }
        })
        .catch((err: any) => {
          console.error(err);
          toast.error("Failed to mark order as done, an error occurred", {
            id: toastId,
          });
        });
    }
  };

  const cancelMarkAsDone = () => {
    setShowDialog(false);
    setSelectedOrder(null);
  };

  const formatOrderDate = (value: string) =>
    new Intl.DateTimeFormat("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(new Date(value));

  const renderOrderDetails = (order: Order) => (
    <div className="p-4">
      <strong>Order Details:</strong>
      {order.items &&
      order.items.length > 0 ? (
        <ul className="list-disc pl-5">
          {order.items.map((item, index) => (
            <li
              className="py-4 list-none"
              key={`${item.product_id}-${index}`}
            >
              <div className="flex items-center gap-2">
                <img
                  src={item.product_details.image_url}
                  alt={item.product_details.name}
                  className="w-12 h-12 object-cover"
                />
                <div>
                  <p className="font-medium">
                    {item.product_details.name}
                    {item.option_name && (
                      <span className="font-normal text-default-600">
                        {" "}
                        · {item.option_name}
                      </span>
                    )}
                  </p>
                  <p>
                    Quantity: {item.quantity}
                    <br />
                    Price: {CURRENCY}
                    {item.price}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>No items in this order.</p>
      )}
      {order.fees && order.fees.length > 0 && (
        <div className="mt-2">
          <strong>Checkout fees:</strong>
          <ul className="list-none">
            {order.fees.map((fee, index) => (
              <li key={`${fee.title}-${index}`}>
                {fee.title}: {CURRENCY}
                {fee.amount}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-4">
        <strong>Delivery Address:</strong>{" "}
        {order.delivery_address}
      </p>
    </div>
  );

  const statusPill = (done: boolean) => (
    <span
      className={
        done
          ? "inline-flex items-center rounded-full bg-success/10 px-2.5 py-1 text-xs font-semibold text-success"
          : "inline-flex items-center rounded-full bg-warning/10 px-2.5 py-1 text-xs font-semibold text-warning"
      }
    >
      {done ? "Done" : "Not Done"}
    </span>
  );

  const columns: ColumnDef<Order>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <div className="xl:w-16">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => {
              row.toggleSelected(!!value);
              // Removed row expansion from checkbox
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click
            }}
            aria-label="Select row"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "recipient_name",
      header: "Customer Name",
      cell: ({ row }) => <span>{row.getValue("recipient_name")}</span>,
    },
    {
      accessorKey: "recipient_phone",
      header: "Customer Phone",
      cell: ({ row }) => <span>{row.getValue("recipient_phone")}</span>,
    },
    {
      accessorKey: "created_at",
      header: "Order Date",
      cell: ({ row }) => <span>{formatOrderDate(row.getValue("created_at"))}</span>,
    },
    {
      accessorKey: "product_cost",
      header: "Paid",
      cell: ({ row }) => {
        return (
          <span>
            {CURRENCY}
            {row.getValue("product_cost")}
          </span>
        );
      },
    },
    {
      accessorKey: "is_done",
      header: "Status",
      cell: ({ row }) => {
        return statusPill(!!row.getValue("is_done"));
      },
    },
    {
      id: "markAsDone",
      header: "Mark as Done",
      cell: ({ row }) => {
        return (
          <Button
            variant="outline"
            size="md"
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click
              handleMarkAsDone(row.original);
            }}
          >
            Mark as Done
          </Button>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      expanded,
    },
  });

  const toggleRowExpansion = (rowId: string) => {
    table.setExpanded((old) => ({
      ...(old as { [key: string]: boolean }),
      [rowId]: !old[rowId],
    }));
  };

  return (
    <div className="w-full">
      <PageHeader title="Pending Orders" />

      <Card className="overflow-hidden">
      <div className="border-b p-4 md:px-5">
        <div className="w-full md:max-w-xs">
          <Input
            placeholder="Search..."
            value={
              (table.getColumn("recipient_name")?.getFilterValue() as string) ??
              ""
            }
            onChange={(event) =>
              table
                .getColumn("recipient_name")
                ?.setFilterValue(event.target.value)
            }
            size="md"
          />
        </div>
      </div>

      {/* Phones: one card per order. */}
      <div className="divide-y md:hidden">
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => {
            const order = row.original;
            return (
              <div key={row.id} className="p-4">
                <button
                  type="button"
                  className="w-full text-start"
                  onClick={() => toggleRowExpansion(row.id)}
                  aria-expanded={row.getIsExpanded()}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-default-900">{order.recipient_name}</div>
                      <div className="mt-0.5 text-sm text-default-600">{order.recipient_phone}</div>
                    </div>
                    <div className="flex-none text-end">
                      <div className="font-display font-semibold text-default-900">
                        {CURRENCY}
                        {order.product_cost}
                      </div>
                      <div className="mt-1">{statusPill(order.is_done)}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-default-500">{formatOrderDate(order.created_at)}</div>
                </button>
                {row.getIsExpanded() && (
                  <div className="mt-3 rounded-[14px] bg-default-50 text-sm text-default-700 [&>div]:p-3">
                    {renderOrderDetails(order)}
                  </div>
                )}
                <Button
                  variant="outline"
                  size="md"
                  className="mt-3 w-full"
                  onClick={() => handleMarkAsDone(order)}
                >
                  Mark as Done
                </Button>
              </div>
            );
          })
        ) : isLoading ? (
          <div className="p-6">
            <Loading />
          </div>
        ) : (
          <EmptyOrders />
        )}
      </div>

      <div className="hidden md:block">
      <Table>
        <TableHeader className="bg-default-50 [&_tr]:border-t-0">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => toggleRowExpansion(row.id)}
                  className="cursor-pointer"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="h-[75px]">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                {/*
                  Wrap the expanded content in TransitionGroup and CSSTransition
                */}
                <TransitionGroup component={null}>
                  {row.getIsExpanded() ? (
                    <CSSTransition
                      key={row.id}
                      timeout={300}
                      classNames="expand"
                    >
                      <TableRow>
                        <TableCell colSpan={columns.length}>
                          {/* Expanded content */}
                          {renderOrderDetails(row.original)}
                        </TableCell>
                      </TableRow>
                    </CSSTransition>
                  ) : null}
                </TransitionGroup>
              </React.Fragment>
            ))
          ) : isLoading ? (
            <TableRow>
              <TableCell className="h-[75px]" colSpan={columns.length}>
                <Loading />
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="p-0">
                <EmptyOrders />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </div>
      <div className="border-t px-4 md:px-5">
        <TablePagination table={table} />
      </div>
      </Card>

      {/* Dialog for Mark as Done */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Order as Done</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to mark this order as done?</p>
          <DialogFooter>
            <Button variant="outline" onClick={cancelMarkAsDone}>
              Cancel
            </Button>
            <Button variant="default" onClick={confirmMarkAsDone}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorboltOrderListingComponent;


/** Shown when there are no pending orders: the two quickest ways to get some. */
function EmptyOrders() {
  const link = useStoreLink();
  const openBoost = useOpenBoost();
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage text-sage-foreground">
        <ShoppingBag className="h-6 w-6" />
      </span>
      <div className="mt-4 font-display text-base font-semibold text-default-900">No pending orders</div>
      <p className="mt-1 max-w-sm text-sm text-default-600">
        New orders show up here as soon as a customer pays. Share your store link or boost it to bring customers in.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {link.url && (
          <Button variant="outline" onClick={link.copy}>
            <Copy className="mr-2 h-4 w-4" />
            Copy Link
          </Button>
        )}
        <Button onClick={() => openBoost()}>
          <Megaphone className="mr-2 h-4 w-4" />
          Boost on Meta Ads
        </Button>
      </div>
    </div>
  );
}
